/**
 * Transport adapters shared by both audience configs (#1731, spec §4.1).
 *
 * Thin call-time wrappers around TRF's existing modules, so unit tests that
 * mock `@/services/api` or `@/services/storage` keep intercepting the exact
 * functions the core calls. Nothing here is audience-voiced.
 */
import axios from 'axios';
import { API_BASE_URL } from '@/lib/constants';
import { apiClient } from '@/services/api';
import { authStorage } from '@/services/storage';
import { VoiceMintError } from '@core/voice/pip/types';
import type { ChatMessageState } from '@/types/chat';

export function getApiOrigin(): string {
  return API_BASE_URL;
}

/** The core appends its own segment (`/stevens-v4`, `/livekit`); mirrors apiClient's baseURL. */
export function getApiBase(): string {
  return `${API_BASE_URL}/api`;
}

export function getAuthHeaders(): Record<string, string> {
  const token = authStorage.peekAccessToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/**
 * TRF has one refresh path: apiClient's response interceptor, which refreshes
 * on a 401 and replays the request. `/auth/me` is the cheapest request that
 * falls through to it, so a resolved call means the token is good (or was
 * just renewed) and a rejection means the session is gone.
 */
export async function refreshAuthToken(): Promise<boolean> {
  try {
    await apiClient.get('/auth/me');
    return true;
  } catch {
    return false;
  }
}

/** Shape the core's caption hydration expects, from TRF's normalized history rows. */
export function toVoiceConversation(messages: ChatMessageState[]) {
  return {
    messages: messages.map((m) => ({ messageId: m.id, content: m.content, role: m.role })),
  };
}

/**
 * Maps a token-route failure onto the three mint failures the PiP widget
 * renders differently. 503 is the agent-dispatch timeout on both token routes.
 */
export function toVoiceMintError(error: unknown): VoiceMintError {
  if (error instanceof VoiceMintError) return error;
  const status = axios.isAxiosError(error) ? error.response?.status : undefined;
  if (status === 503) {
    return new VoiceMintError('agent-absent', 'The voice agent did not answer the dispatch.');
  }
  if (status) return new VoiceMintError('unavailable', `Voice is unavailable (${status}).`);
  return new VoiceMintError('network', 'Could not reach the server.');
}

export function assertGrant(body: { token?: string; url?: string; roomName?: string; conversationId?: string }) {
  if (!body?.token || !body?.url || !body?.roomName) {
    throw new VoiceMintError('unavailable', 'Voice is unavailable.');
  }
  // The thread id rides beside the room name: TRF's token routes return
  // both, and the thread (not the room) is the record the transcript lands in.
  return { token: body.token, url: body.url, roomName: body.roomName, conversationId: body.conversationId };
}

/**
 * A 409 from the token route means the user's Redis active-session key is
 * still set — a session that ended without its DELETE (tab closed, webhook
 * unreachable). Clear it once and let the caller retry, the rule the legacy
 * `useVoiceSession` applied.
 */
export function isStaleSessionConflict(error: unknown): boolean {
  return axios.isAxiosError(error) && error.response?.status === 409;
}
